import dayjs from 'dayjs';
import { Box, Card, CardBody, CardFooter, CardHeader, Pagination, Text } from 'grommet';
import { Share } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { notificationLink } from 'src/utils/notificationLink';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotificationStore, useProjectStore } from 'src/context';
import { tableHeaderTextProps } from '../TableHeaderItem';
import { NotificationIcon } from './NotificationIcon';
import { EmptyState } from '@talon/web/src/components/ui/EmptyState';

export const NotificationsTable = observer(() => {
  /** Context **/
  const notificationStore = useNotificationStore();
  const projectStore = useProjectStore();
  const linkFor = (n: { source_entity_name?: string; source_entity_id?: string }) =>
    notificationLink(n, { currentProjectName: projectStore.currentProject?.name, projects: projectStore.projectList });

  /** State **/
  const [isLoading, setIsLoading] = useState(true);

  /** Refs **/
  const columnWidths = [
    '3%', // type icon
    '16%', // timestamp
    '81%', // details
  ];

  /** Effects **/

  useEffect(() => {
    if (isLoading) notificationStore.list().finally(() => setIsLoading(false));
  }, [isLoading]);

  useEffect(() => {
    if (notificationStore.page) setIsLoading(true);
  }, [notificationStore.page]);

  useEffect(() => {
    return () => {
      notificationStore.markAsRead();
    };
  }, []);

  /** Render */
  return (
    <Card
      round="10px"
      border={{ color: 'border-card' }}
      elevation="none"
      background="white"
      animation={{ type: 'fadeIn', duration: 500 }}
    >
      {/* Header */}
      <CardHeader
        justify="start"
        align="center"
        gap="small"
        background="white"
        border={{ side: 'bottom', color: 'border-divider' }}
        height="52px"
        pad={{ horizontal: '14px' }}
      >
        <Text size="17px" weight={600} color="text-heading">
          Notifications
        </Text>
      </CardHeader>

      {/* Body */}
      <CardBody background="white" elevation="xsmall">
        {/* Table Header */}
        <Box
          direction="row"
          justify="between"
          align="center"
          gap="medium"
          pad={{ vertical: 'xsmall', horizontal: 'small' }}
          background="surface-header"
          border={{ side: 'bottom', color: 'border-card' }}
        >
          <Box width={columnWidths[0]} align="center">
            <Text {...tableHeaderTextProps}>Type</Text>
          </Box>
          <Box width={columnWidths[1]}>
            <Text {...tableHeaderTextProps}>Time</Text>
          </Box>
          <Box width={columnWidths[2]}>
            <Text {...tableHeaderTextProps}>Details</Text>
          </Box>
        </Box>

        {notificationStore.notifications.length > 0 ? (
          notificationStore.notifications.map((n, i) => (
            <Box
              key={n.id}
              border={
                i < notificationStore.notifications.length - 1
                  ? { side: 'bottom', size: '1px', color: 'light-3' }
                  : undefined
              }
              direction="row"
              pad={{ horizontal: 'small', vertical: '1.1rem' }}
              gap="medium"
              align="center"
              justify="center"
              fill="horizontal"
            >
              <Box width={columnWidths[0]} align="center">
                <NotificationIcon notification={n} />
              </Box>
              <Box width={columnWidths[1]}>
                <Text>{dayjs(n.created_at).format('MMM D, YYYY @ h:mm:ss A')}</Text>
              </Box>
              <Box width={columnWidths[2]}>
                {linkFor(n) ? (
                  <Link to={linkFor(n) ?? ''}>
                    <Box direction="row" gap="xsmall" align="center">
                      <Text truncate>{n.text}</Text>
                      <Share size="small" />
                    </Box>
                  </Link>
                ) : (
                  <Text truncate>{n.text}</Text>
                )}
              </Box>
            </Box>
          ))
        ) : (
          <EmptyState title="No notifications" body="You're all caught up." />
        )}
      </CardBody>

      {/* Footer */}
      <CardFooter justify="center" gap="small" background="light-2" pad="small">
        <Pagination
          page={notificationStore.page}
          step={notificationStore.limit}
          numberItems={notificationStore.total}
          onChange={(props) => notificationStore.setPage(props.page)}
        />
      </CardFooter>
    </Card>
  );
});
